/**
 * Features Registry Types
 *
 * Feature-first permissions model. The features registry (stored in
 * the database) is the source of truth for what features exist, which
 * permissions live under each feature, and what the default role grants
 * look like.
 */
import { z } from 'zod';
// =============================================================================
// Role
// =============================================================================
export const RoleSchema = z.enum(['admin', 'sales-rep', 'system']);
// Display metadata for UI. Record<Role, ...> so a new role is a compile error
// until it gets a label here.
export const RoleMetadata = {
    admin: { label: 'Admin' },
    'sales-rep': { label: 'Sales Rep' },
    system: { label: 'System' },
};
/**
 * Whether a user's role is a post-meeting coaching target.
 *
 * Coaching is per-sales-rep: it scores an individual's turns in a meeting.
 * Admins join meetings (and get live flashcards + the shared meeting
 * analysis) but are never coached. `'system'` is a non-human actor and is
 * likewise excluded. Any other / unknown / null role defaults to coachable -
 * only the clearly non-rep roles are excluded.
 */
export function isCoachableRole(role) {
    return role !== 'admin' && role !== 'system';
}
// =============================================================================
// Permission descriptor (lives under a feature)
// =============================================================================
// =============================================================================
// Canonical feature and permission IDs
// =============================================================================
export const FEATURE_KEYS = {
    AUDIT: 'audit',
    KNOWLEDGE_BASE: 'knowledge-base',
    USER_MANAGEMENT: 'user-management',
    EMAILS: 'emails',
    CLIENTS: 'clients',
    CONTACTS: 'contacts',
    MEETINGS: 'meetings',
    OVERLAY: 'overlay',
    DEALS: 'deals',
    COACHING: 'coaching',
    ACTIONS: 'actions',
    PIPELINES: 'pipelines',
    ORG_WIDE_BROWSE: 'org-wide-browse',
    CRM_INTEGRATIONS: 'crm-integrations',
    CUSTOM_FIELDS: 'custom-fields',
    CUSTOM_DATA_MODELS: 'custom-data-models',
    QUOTAS: 'quotas',
    WIN_LOSS: 'win-loss',
    REPORTS: 'reports',
    AI_FORECAST: 'ai-forecast',
};
export const KNOWN_FEATURES = Object.values(FEATURE_KEYS);
export const PERMISSIONS = {
    AUDIT_ACCESS: 'audit:access',
    AUDIT_EXPORT: 'audit:export',
    USER_MANAGEMENT_ACCESS: 'user-management:access',
    KNOWLEDGE_BASE_READ: 'knowledge-base:read',
    KNOWLEDGE_BASE_WRITE: 'knowledge-base:write',
    EMAILS_READ: 'emails:read',
    EMAILS_WRITE: 'emails:write',
    OVERLAY_USE: 'overlay:use',
    MEETINGS_READ: 'meetings:read',
    MEETINGS_WRITE: 'meetings:write',
    CLIENTS_READ: 'clients:read',
    CLIENTS_WRITE: 'clients:write',
    CONTACTS_READ: 'contacts:read',
    CONTACTS_WRITE: 'contacts:write',
    DEALS_READ: 'deals:read',
    DEALS_WRITE: 'deals:write',
    ACTIONS_READ: 'actions:read',
    ACTIONS_WRITE: 'actions:write',
    ORG_WIDE_BROWSE_ACCESS: 'org-wide-browse:access',
    PIPELINES_WRITE: 'pipelines:write',
    COACHING_READ: 'coaching:read',
    COACHING_WRITE: 'coaching:write',
    CRM_INTEGRATIONS_ACCESS: 'crm-integrations:access',
    CRM_INTEGRATIONS_READ: 'crm-integrations:read',
    CUSTOM_FIELDS_MANAGE: 'custom-fields:manage',
    CUSTOM_FIELDS_FILL: 'custom-fields:fill',
    CUSTOM_OBJECTS_READ: 'custom-objects:read',
    CUSTOM_OBJECTS_WRITE: 'custom-objects:write',
    QUOTAS_MANAGE: 'quotas:manage',
    QUOTAS_READ: 'quotas:read',
    REPORTS_READ: 'reports:read',
    REPORTS_WRITE: 'reports:write',
};
export const KNOWN_PERMISSION_IDS = Object.values(PERMISSIONS);
export function isKnownPermissionId(permissionId) {
    return KNOWN_PERMISSION_IDS.includes(permissionId);
}
export const PermissionIdSchema = z.enum(KNOWN_PERMISSION_IDS);
export const PermissionDescriptorSchema = z.object({
    id: PermissionIdSchema,
    label: z.string(),
    description: z.string(),
});
// =============================================================================
// Feature
// =============================================================================
export const FeatureSchema = z.object({
    feature: z.string(),
    label: z.string(),
    description: z.string(),
    icon: z.string(),
    baseline: z.boolean(),
    allowedRoles: z.array(RoleSchema),
    sortOrder: z.number(),
    permissions: z.array(PermissionDescriptorSchema),
    roleDefaults: z.partialRecord(RoleSchema, z.array(PermissionIdSchema)),
    updatedAt: z.string(),
});
// =============================================================================
// Registry
// =============================================================================
export const FeaturesRegistrySchema = z.array(FeatureSchema);
